import { Grid, Typography } from "@mui/material";
import {
	fetchLicenseGroups,
	fetchLicenseSubscriptions,
	fetchSubscribedSkus,
} from "actions/licenseActions";
import { fetchLicenseNames } from "actions/licenseNameActions";
import { fetchScheduledActions } from "actions/scheduledActionActions";
import { licenseNameSelectors, selectlicenseNames } from "features/licenses/licenseNames";
import {
	selectActiveLicenseManagementId,
	selectLicenseGroups,
	selectLicenseSubscriptions,
	selectLicenseSubscriptionsWithConsumptionDetails,
	selectSubscribedSkus,
} from "features/licenses/licenses";
import { selectLicenseActions } from "features/scheduledActions";
import {
	useApiOnce,
	useApiWithAndWithoutCache,
	useAppSelector,
	useFetchLicensePrices,
} from "hooks/hooks";
import { useMemo } from "react";
import { NAVIGATION_NAMES } from "utilities/constants/pages";
import { LicenseAssignmentView } from "./LicenseAssignmentView";
import { LicenseCard } from "./LicenseCard";
import { LicenseDetailsView } from "./LicenseDetailsView";
import { LicenseManagementButtonSection } from "./LicenseManagementButtonSection";
import styles from "./LicenseManagementView.module.scss";

export const LicenseManagementView = ({ page }: { page: string }) => {
	const licenseState = useAppSelector(selectLicenseSubscriptions);
	const subscribedSkus = useAppSelector(selectSubscribedSkus);
	const licenseNameState = useAppSelector(selectlicenseNames);
	useApiOnce(fetchScheduledActions, useAppSelector(selectLicenseActions));
	useApiOnce(fetchSubscribedSkus, subscribedSkus);
	useApiWithAndWithoutCache(fetchLicenseSubscriptions, licenseState);
	useApiOnce(fetchLicenseGroups, useAppSelector(selectLicenseGroups));
	useApiOnce(fetchLicenseNames, licenseNameState);
	useFetchLicensePrices();

	const licenses = useAppSelector(selectLicenseSubscriptionsWithConsumptionDetails);
	const activeLicenseManagementId = useAppSelector(selectActiveLicenseManagementId);
	const licenseNames = useAppSelector(licenseNameSelectors.selectEntities);

	const isLoading =
		licenseState.isLoading || subscribedSkus.isLoading || licenseNameState.isLoading;

	const content = useMemo(() => {
		switch (page) {
			case NAVIGATION_NAMES.LICENSE_MANAGEMENT.path:
				return activeLicenseManagementId === "" ? (
					<Grid container className={styles.cardsContainer}>
						<Grid container item className={styles.titleSectionContainer}>
							<Grid container item className={styles.titleSection}>
								<Typography variant="h2">Manage Licenses</Typography>
								<LicenseManagementButtonSection />
							</Grid>
						</Grid>
						{!isLoading
							? licenses.map((license) => (
									<LicenseCard
										key={license.provisioningId}
										name={
											licenseNames[license.provisioningId]
												?.licenseDisplayName ?? license.friendlyName
										}
										licenseVariants={license.variants}
										used={license.consumedQuantity}
										total={license.totalQuantity}
										isLoadingSubscriptions={subscribedSkus.isLoading}
									/>
								))
							: Array.from({ length: 12 }, (_, i) => i).map((_, i) => (
									<LicenseCard
										key={i}
										name="Microsoft 365 E5"
										licenseVariants={[]}
										used={0}
										total={0}
										isLoadingSubscriptions={true}
									/>
								))}
					</Grid>
				) : (
					<Grid container item className={styles.detailsWrapper}>
						<LicenseDetailsView />
					</Grid>
				);
			case NAVIGATION_NAMES.ASSIGN_LICENSES.path:
				return <LicenseAssignmentView />;
			default:
				return null;
		}
	}, [
		page,
		activeLicenseManagementId,
		licenses,
		subscribedSkus.isLoading,
		licenseNames,
		isLoading,
	]);

	return (
		<Grid container item className={styles.wrapper}>
			{content}
		</Grid>
	);
};
